import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { subDays } from 'date-fns';
import apiManager from '../../api/api-manager';
import {
  AnnullaCambioContattoTipoContattoEnum,
  DocumentoScadutoError,
  DocumentoScadutoErrorCodiciErroreEnum,
  PersonaTipoUtenteEnum,
  PreparaCambioContattoResponse,
  PreparaCambioContattoTipoContattoEnum,
  SimpleToken,
  StatusUtente,
} from '../../api/generated';
import { parseServerDateTime } from '../../api/parsers/commons-api-parsers';
import { env } from '../../env';
import { ApiRequestStatus } from '../../model/api-request';
import signatureAreaRepository from '../../repositories/signature-area/signature-area-repository';
import { getToday } from '../../utils';
import {
  getAPIRequestHeaders,
  getAPIRequestHeadersWithToken,
} from '../../utils/api-utils';
import { AsyncThunkConfig } from '../async-thunk-config';
import { initializeApp } from 'firebase/app';
import { getAuth, SAMLAuthProvider, signInWithPopup } from 'firebase/auth';
import { GetRubricheError } from '../features/user-access';
import { ApiErrorCodes, parseApiResponseError } from '../../api/api-error';
import { resetChangePhoneNumberState } from '../features/profile';
import { TipoUtenteEnum } from '../../model';

// The informative users cannot sign, cannot access the profile and their documents are not marked as read.
// For now they are only the fiduciante users created from the admin console (username FDN...).
const INFORMATIVE_USERNAME_PREFIXES = [TipoUtenteEnum.Fdn.toUpperCase()];

export const isInformativeUsername = (username?: string): boolean =>
  INFORMATIVE_USERNAME_PREFIXES.some(
    (prefix) => !!username?.toUpperCase().startsWith(prefix)
  );

export interface UserState {
  token?: string;
  tokenRubrica?: string;
  username: string;
  prospectToken?: string;
  signFiles: boolean;
  signFilesEmail: boolean;
  signFilesPhoneNumber: boolean;
  disponibileCambioContatti: boolean;
  bundleId?: string;
  signFilesRequestStatus: ApiRequestStatus;
  setContextStatus: ApiRequestStatus;
  signFilesError?: SignFilesError;
  email: string;
  emailUpdatedDate: Date;
  phone: string;
  phoneUpdatedDate: Date;
  secondaryPhone?: string;
  userType: PersonaTipoUtenteEnum;
  userCode?: string;
  prospectCode?: string;
  userBehalfOfCode?: string;
  codiceProspectRappresentante?: string;
  name: string;
  surname: string;
  businessName: string;
  hideBannerCookiePolicy: boolean;
  informativeMode: boolean;
  enableMarketplaceMobile?: boolean;
  isProspect: boolean;
  taxCode: string;
  definitivo: boolean;
  referral: string;
  prepareChangeContactRequestStatus?: ApiRequestStatus;
  prepareChangeContactError?: ChangeContactError;
  abortChangeContactRequestStatus?: ApiRequestStatus;
  abortChangeContactError?: ChangeContactError;
  fiduciaria: boolean;
  codiceRubrica: string;
  changeContactsAvailable: boolean;
  pendingNewEmail?: string;
  pendingNewPhone?: string;
  codiceAgente: string;
  documentiScaduti: boolean;
  showBalance: boolean;
}

const initialState: UserState = {
  username: null,
  signFiles: false,
  signFilesEmail: false,
  signFilesPhoneNumber: false,
  disponibileCambioContatti: false,
  signFilesRequestStatus: 'idle',
  setContextStatus: 'idle',
  email: null,
  emailUpdatedDate: null,
  phone: null,
  phoneUpdatedDate: null,
  name: null,
  surname: null,
  businessName: null,
  userType: null,
  hideBannerCookiePolicy: false,
  informativeMode: false,
  isProspect: false,
  taxCode: null,
  definitivo: false,
  referral: undefined,
  fiduciaria: false,
  codiceRubrica: '',
  prepareChangeContactRequestStatus: 'idle',
  changeContactsAvailable: false,
  pendingNewEmail: undefined,
  pendingNewPhone: undefined,
  codiceAgente: null,
  documentiScaduti: false,
  showBalance: false,
};

const userSlice = createSlice({
  name: 'data/user',
  initialState,
  reducers: {
    resetUserState() {
      return initialState;
    },
    updateToken(state, action: PayloadAction<string>) {
      state.token = action.payload;
    },
    updateProspectToken(state, action: PayloadAction<string>) {
      state.prospectToken = action.payload;
    },
    updateUsername(state, action: PayloadAction<string>) {
      state.username = action.payload;
      state.informativeMode = isInformativeUsername(action.payload);
      state.emailUpdatedDate = action.payload?.toUpperCase().includes('APC-347') // TODO: remove it when this data will be returned from the BE
        ? subDays(getToday(), 4)
        : subDays(getToday(), 6);
      state.phoneUpdatedDate = action.payload?.toUpperCase().includes('APC-347') // TODO: remove it when this data will be returned from the BE
        ? subDays(getToday(), 4)
        : subDays(getToday(), 6);
    },
    updateHideBannerCookiePolicy(state, action: PayloadAction<boolean>) {
      state.hideBannerCookiePolicy = action.payload;
    },
    setEnableMarketplace(state, action: PayloadAction<boolean>) {
      state.enableMarketplaceMobile = action.payload;
    },
    updateIsProspect(state, action: PayloadAction<boolean>) {
      state.isProspect = action.payload;
    },
    updateShowBalance(state, action: PayloadAction<boolean>) {
      state.showBalance = action.payload;
    },
    updateReferral(state, action: PayloadAction<string>) {
      state.referral = action.payload;
    },
    setRubrica(state, action: PayloadAction<string>) {
      state.codiceRubrica = action.payload;
    },
    updateIsFiduciaria(state, action: PayloadAction<boolean>) {
      state.fiduciaria = action.payload;
    },
    resetApiContextStatus(state) {
      state.setContextStatus = 'idle';
    },
    resetPrepareChangeContactState(state) {
      state.prepareChangeContactRequestStatus = 'idle';
      state.prepareChangeContactError = undefined;
    },
    clearPendingContacts(state) {
      state.pendingNewEmail = undefined;
      state.pendingNewPhone = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getStatusUtenza.pending, (state) => {
      state.signFilesRequestStatus = 'loading';
    });
    builder.addCase(getStatusUtenza.fulfilled, (state, action) => {
      const isPg = state.userType === PersonaTipoUtenteEnum.Pg;
      state.signFilesRequestStatus = 'succeeded';
      if (action.payload) {
        state.signFilesEmail = action.payload.necessariaFirmaCambioEmail;
        state.signFilesPhoneNumber =
          action.payload.necessariaFirmaCambioCellulare;
        state.username = action.payload.codiceUtente;
        state.informativeMode = isInformativeUsername(
          action.payload.codiceUtente
        );
        state.disponibileCambioContatti =
          action.payload.disponibileCambioContatti;
        state.bundleId = action.payload.bundleId;
        state.email = action.payload.email;
        state.phone = action.payload.cellulare;
        state.secondaryPhone = action.payload.cellulareSecondario;
        state.userType = action.payload.tipoUtente;
        state.userCode = isPg
          ? action.payload.codiceClienteRappresentante
          : action.payload.codiceCliente;
        state.prospectCode = action.payload.codiceProspect;
        state.userBehalfOfCode = isPg
          ? action.payload.codiceCliente
          : undefined;
        state.codiceProspectRappresentante = isPg
          ? action.payload.codiceProspectRappresentante
          : undefined;
        action.payload.codiceProspectRappresentante;
        state.name = action.payload.nome;
        state.surname = action.payload.cognome;
        state.businessName = action.payload.ragioneSociale;
        state.taxCode = action.payload.codiceFiscale;
        state.emailUpdatedDate = parseServerDateTime(
          action.payload.ultimoAggiornamentoEmail
        );
        state.phoneUpdatedDate = parseServerDateTime(
          action.payload.ultimoAggiornamentoCellulare
        );
        state.enableMarketplaceMobile = false;
        state.definitivo = action.payload.definitivo || false;
        state.changeContactsAvailable =
          action.payload.disponibileCambioContatti ?? false;
        state.codiceAgente = action.payload.codiceAgente ?? null;
        state.documentiScaduti = action.payload.documentiScaduti ?? false;
      }
    });
    builder.addCase(getStatusUtenza.rejected, (state, action) => {
      state.signFilesError = action.payload;
      state.signFilesRequestStatus = 'failed';
    });
    builder.addCase(countDispositionToSign.pending, (state) => {
      state.signFilesRequestStatus = 'loading';
    });
    builder.addCase(countDispositionToSign.fulfilled, (state, action) => {
      state.signFiles = !!action.payload;
      state.signFilesRequestStatus = 'idle';
    });
    builder.addCase(countDispositionToSign.rejected, (state, action) => {
      state.signFilesError = action.payload;
      state.signFilesRequestStatus = 'failed';
    });
    builder.addCase(prepareChangeContact.fulfilled, (state, action) => {
      state.prepareChangeContactRequestStatus = 'succeeded';

      const { contact, contactType } = action.meta.arg;
      if (contactType === PreparaCambioContattoTipoContattoEnum.Email) {
        state.pendingNewEmail = contact;
      } else if (contactType === PreparaCambioContattoTipoContattoEnum.Phone) {
        state.pendingNewPhone = contact;
      }
    });
    builder.addCase(prepareChangeContact.rejected, (state, action) => {
      state.prepareChangeContactRequestStatus = 'failed';
      state.prepareChangeContactError = action.payload;
    });
    builder.addCase(prepareChangeContact.pending, (state) => {
      state.prepareChangeContactRequestStatus = 'loading';
    });
    builder.addCase(abortChangeContact.fulfilled, (state) => {
      state.abortChangeContactRequestStatus = 'succeeded';
    });
    builder.addCase(abortChangeContact.rejected, (state, action) => {
      state.abortChangeContactRequestStatus = 'failed';
      state.abortChangeContactError = action.payload;
    });
    builder.addCase(abortChangeContact.pending, (state) => {
      state.abortChangeContactRequestStatus = 'loading';
    });
    builder.addCase(setContext.pending, (state) => {
      state.setContextStatus = 'loading';
    });
    builder.addCase(setContext.fulfilled, (state, action) => {
      state.tokenRubrica = action.payload.token;
      state.setContextStatus = 'succeeded';
    });
    builder.addCase(setContext.rejected, (state) => {
      state.setContextStatus = 'failed';
    });
  },
});

export const {
  resetUserState,
  updateToken,
  updateProspectToken,
  updateUsername,
  updateHideBannerCookiePolicy,
  setEnableMarketplace,
  updateIsProspect,
  updateShowBalance,
  updateReferral,
  setRubrica,
  updateIsFiduciaria,
  resetApiContextStatus,
  resetPrepareChangeContactState,
  clearPendingContacts,
} = userSlice.actions;

export default userSlice.reducer;

export type SignFilesError = 'GENERIC_ERROR';

export type ChangeContactError = 'GENERIC_ERROR' | 'DOCUMENTO_SCADUTO';

export const getStatusUtenza = createAsyncThunk<
  StatusUtente,
  void,
  AsyncThunkConfig<SignFilesError>
>('status_utenza', async (_, thunkApi) => {
  try {
    const userState = thunkApi.getState().data.user;
    const response = await apiManager.statusApi.statusUtenza(
      userState.username,
      getAPIRequestHeaders(thunkApi)
    );
    return response.data;
  } catch (error) {
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

type PrepareChangeContactPayload = {
  contact: string;
  oldContact: string;
  contactType: PreparaCambioContattoTipoContattoEnum;
};

export const prepareChangeContact = createAsyncThunk<
  PreparaCambioContattoResponse,
  PrepareChangeContactPayload,
  AsyncThunkConfig<ChangeContactError>
>(
  'prepare_change_contact',
  async (
    { contact, oldContact, contactType }: PrepareChangeContactPayload,
    thunkApi
  ) => {
    try {
      const userState = thunkApi.getState().data.user;

      const response =
        await apiManager.prepareChangeContact.preparaCambioContatto(
          userState.username,
          {
            contatto: contact,
            vecchio_contatto: oldContact,
            tipo_contatto: contactType,
          },
          getAPIRequestHeaders(thunkApi)
        );

      thunkApi.dispatch(getStatusUtenza());

      return response.data;
    } catch (error) {
      console.error(error);
      const apiError = parseApiResponseError<DocumentoScadutoError>(error);
      const errorCodes = apiError.payload?.codiciErrore;

      if (apiError.responseErrorCode === ApiErrorCodes.BAD_REQUEST) {
        if (errorCodes?.length > 0) {
          switch (errorCodes[0]) {
            case DocumentoScadutoErrorCodiciErroreEnum.DocumentoScaduto:
              return thunkApi.rejectWithValue('DOCUMENTO_SCADUTO');
          }
        }
      }
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
);

type AbortChangeContactPayload = {
  contactType: AnnullaCambioContattoTipoContattoEnum;
};
export const abortChangeContact = createAsyncThunk<
  void,
  AbortChangeContactPayload,
  AsyncThunkConfig<ChangeContactError>
>(
  'abort_change_contact',
  async ({ contactType }: AbortChangeContactPayload, thunkApi) => {
    try {
      const userState = thunkApi.getState().data.user;
      const response =
        await apiManager.prepareChangeContact.annullaCambioContatto(
          userState.username,
          {
            tipo_contatto: contactType,
          },
          getAPIRequestHeaders(thunkApi)
        );
      thunkApi.dispatch(resetChangePhoneNumberState());
      thunkApi.dispatch(resetPrepareChangeContactState());
      thunkApi.dispatch(getStatusUtenza());
      return response.data;
    } catch (error) {
      console.error(error);
      const apiError = parseApiResponseError<DocumentoScadutoError>(error);
      const errorCodes = apiError.payload?.codiciErrore;

      if (apiError.responseErrorCode === ApiErrorCodes.BAD_REQUEST) {
        if (errorCodes?.length > 0) {
          switch (errorCodes[0]) {
            case DocumentoScadutoErrorCodiciErroreEnum.DocumentoScaduto:
              return thunkApi.rejectWithValue('DOCUMENTO_SCADUTO');
          }
        }
      }
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
);

export const countDispositionToSign = createAsyncThunk<
  number,
  void,
  AsyncThunkConfig<SignFilesError>
>('count_disposition_to_sign', async (_, thunkApi) => {
  try {
    const response = await signatureAreaRepository.downloadSignroomDocuments(
      thunkApi
    );
    return response.filter(
      (item) =>
        item.type.practiceType !== 'ContactData' &&
        item.type.practiceType !== 'DigitalServiceMembership'
    ).length;
  } catch (error) {
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const financialAdvisorLogin = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig<SignFilesError>
>('financial-advisor-login', async (_, thunkApi) => {
  try {
    const config = {
      apiKey: env.firebaseApiKey,
      authDomain: env.firebaseAuthDomain,
    };
    const provider = new SAMLAuthProvider(env.firebaseProvider);
    const firebaseApp = initializeApp(config);
    const auth = getAuth(firebaseApp);
    const user = await signInWithPopup(auth, provider);
    const credential = await user?.user?.getIdTokenResult();
    const email = credential?.claims?.firebase?.identities?.email[0] as any;

    const response = await apiManager.agentsApi.searchAgent(
      email,
      getAPIRequestHeadersWithToken(thunkApi, credential.token)
    );
    const isCF = response.data;

    if (user && isCF) {
      thunkApi.dispatch(userSlice.actions.updateToken(credential.token));
    } else {
      alert('Utente non abilitato');
    }
    return null;
  } catch (error) {
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const setContext = createAsyncThunk<
  SimpleToken,
  string,
  AsyncThunkConfig<GetRubricheError>
>('set-context', async (codiceRubrica: string, thunkApi) => {
  try {
    const res = await apiManager.fiduciarieApi.setContext(
      { codiceRubrica },
      getAPIRequestHeaders(thunkApi)
    );
    return res.data;
  } catch (error) {
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});
